import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shield, ShieldOff, CheckCircle2, XCircle, Loader2, ArrowLeft, ExternalLink, AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Zero-Trust Audit Chain viewer.
 * - Fetches the public chain from /api/audit/public-chain
 * - Client-side recomputes SHA-256 of each event's canonical_bytes
 * - Verifies event N's prev_hash === event N-1's event_hash
 * - No server-side "trust me" — everything is verifiable in-browser.
 */
async function sha256Hex(str) {
  const bytes = new TextEncoder().encode(str);
  const buf = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function shortHash(h, front = 8, back = 6) {
  if (!h) return "genesis";
  if (h.length <= front + back + 3) return h;
  return `${h.slice(0, front)}…${h.slice(-back)}`;
}

function fmtTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + " UTC";
  } catch {
    return iso;
  }
}

export default function PublicAudit() {
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [verifyState, setVerifyState] = useState("idle"); // idle|running|ok|broken
  const [verifyDetail, setVerifyDetail] = useState(null);
  const [rowStatus, setRowStatus] = useState({}); // idx -> "ok"|"broken"|"pending"
  const [limit, setLimit] = useState(50);
  const [tamperMode, setTamperMode] = useState(false); // demo mode: injected forgery
  const [tamperInfo, setTamperInfo] = useState(null); // { index, field, original, mutated }

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setVerifyState("idle");
    setRowStatus({});
    setTamperMode(false);
    setTamperInfo(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/audit/public-chain?limit=${limit}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setChain(d);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  /**
   * TAMPER DRILL — Client-side only.
   * Simulates a forgery by mutating one event's canonical_bytes in memory.
   * The chain becomes cryptographically invalid → verification MUST detect it.
   * This is a demonstration that our attestation actually works.
   */
  const runTamperDrill = useCallback(() => {
    if (!chain?.events?.length) return;
    const events = chain.events.map((e) => ({ ...e }));
    // Pick a random middle event (not first, not last) for maximum ripple visibility
    const targetIdx = Math.max(1, Math.min(events.length - 2, Math.floor(events.length * 0.4)));
    const original = events[targetIdx].canonical_bytes;
    // Mutate: prefer a visually obvious change in the first 200 chars
    let mutated;
    if (original.includes('"BUY"')) {
      mutated = original.replace('"BUY"', '"SELL"');
    } else if (original.includes('"SELL"')) {
      mutated = original.replace('"SELL"', '"BUY"');
    } else if (original.match(/"fill_price":([0-9.]+)/)) {
      mutated = original.replace(/"fill_price":([0-9.]+)/, (_, p) => `"fill_price":${(parseFloat(p) + 1).toFixed(5)}`);
    } else if (original.match(/"entry":([0-9.]+)/)) {
      mutated = original.replace(/"entry":([0-9.]+)/, (_, p) => `"entry":${(parseFloat(p) + 0.5).toFixed(5)}`);
    } else {
      // Fallback: inject a visible marker in the first 100 chars so the diff is obvious
      const insertAt = Math.min(80, Math.max(20, original.indexOf(":") + 1));
      mutated = original.slice(0, insertAt) + '"TAMPERED":true,' + original.slice(insertAt);
    }
    events[targetIdx] = { ...events[targetIdx], canonical_bytes: mutated };
    setChain({ ...chain, events });
    setTamperMode(true);
    setTamperInfo({ index: targetIdx, original, mutated });
    setVerifyState("idle");
    setRowStatus({});
    // Auto-run verification to reveal the drama
    setTimeout(() => {
      // Use ref-like closure: re-verify inside timeout with the mutated data
      const runVerify = async () => {
        setVerifyState("running");
        setVerifyDetail(null);
        setRowStatus({});
        let prev = "genesis";
        for (let i = 0; i < events.length; i++) {
          const ev = events[i];
          const recomputed = await sha256Hex(ev.canonical_bytes);
          const prevOk = i === 0 ? (ev.prev_hash === "genesis" || ev.prev_hash === prev) : ev.prev_hash === prev;
          const hashOk = recomputed === ev.event_hash;
          if (!hashOk || !prevOk) {
            setRowStatus((s) => ({ ...s, [i]: "broken" }));
            setVerifyState("broken");
            setVerifyDetail({
              broken_index: i, expected_prev_hash: prev, got_prev_hash: ev.prev_hash,
              expected_event_hash: recomputed, got_event_hash: ev.event_hash,
              prev_ok: prevOk, hash_ok: hashOk,
              tamper_drill: true,
            });
            return;
          }
          setRowStatus((s) => ({ ...s, [i]: "ok" }));
          prev = ev.event_hash;
        }
      };
      runVerify();
    }, 150);
  }, [chain]);

  const verify = useCallback(async () => {
    if (!chain?.events?.length) return;
    setVerifyState("running");
    setVerifyDetail(null);
    setRowStatus({});
    let prev = "genesis";
    for (let i = 0; i < chain.events.length; i++) {
      const ev = chain.events[i];
      const recomputed = await sha256Hex(ev.canonical_bytes);
      const prevOk = i === 0
        ? (ev.prev_hash === "genesis" || ev.prev_hash === prev)  // first row : may be legacy
        : ev.prev_hash === prev;
      const hashOk = recomputed === ev.event_hash;
      if (!hashOk || !prevOk) {
        setRowStatus((s) => ({ ...s, [i]: "broken" }));
        setVerifyState("broken");
        setVerifyDetail({
          broken_index: i,
          expected_prev_hash: prev,
          got_prev_hash: ev.prev_hash,
          expected_event_hash: recomputed,
          got_event_hash: ev.event_hash,
          prev_ok: prevOk,
          hash_ok: hashOk,
        });
        return;
      }
      setRowStatus((s) => ({ ...s, [i]: "ok" }));
      prev = ev.event_hash;
    }
    setVerifyState("ok");
    setVerifyDetail({ n_verified: chain.events.length, final_hash: prev });
  }, [chain]);

  return (
    <div className="min-h-screen bg-bg text-white" data-testid="public-audit-page">
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="audit-home-link">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Public Audit Chain</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5" data-testid="audit-back-link">
              <ArrowLeft size={14} /> Retour
            </Link>
            <Link to="/pricing" className="btn btn-primary text-xs px-4 py-2" data-testid="audit-pricing-link">
              Tarifs
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* HERO */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-up/10 border border-up/30 rounded-full text-xs uppercase tracking-widest text-up mb-4">
            <Shield size={12} /> Zero-Trust Attestation · Vérification client-side
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight max-w-3xl">
            La chaîne d&apos;audit ANATRADEX, <span className="text-accent-blue">vérifiable dans votre navigateur</span>.
          </h1>
          <p className="mt-6 text-white/60 max-w-3xl leading-relaxed">
            Chaque signal généré, ordre exécuté et changement de configuration est immuablement scellé
            dans une hash-chain SHA-256. Aucune confiance en nous n&apos;est requise : nous exposons les <b>bytes canoniques exacts</b> utilisés
            pour calculer chaque hash. Votre navigateur les re-hashera avec <code className="text-accent-blue">crypto.subtle.digest</code> et confirmera l&apos;intégrité end-to-end.
          </p>
        </div>

        {/* CONTROLS */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            onClick={verify}
            disabled={verifyState === "running" || !chain}
            className="btn btn-primary text-sm px-5 py-2.5 disabled:opacity-40"
            data-testid="audit-verify-btn"
          >
            {verifyState === "running" ? (
              <><Loader2 size={14} className="animate-spin" /> Vérification…</>
            ) : (
              <><Shield size={14} /> Vérifier la chaîne ({chain?.events?.length || 0})</>
            )}
          </button>
          <button
            onClick={runTamperDrill}
            disabled={!chain || tamperMode}
            className="btn text-sm px-4 py-2.5 border-warn/50 text-warn hover:bg-warn/10 disabled:opacity-40"
            data-testid="audit-tamper-drill-btn"
            title="Simule une falsification côté client pour prouver que la vérification la détecte"
          >
            <ShieldOff size={14} /> Simuler une falsification
          </button>
          {tamperMode && (
            <button
              onClick={load}
              className="btn text-sm px-4 py-2.5 border-up/50 text-up hover:bg-up/10"
              data-testid="audit-reset-btn"
            >
              <RotateCcw size={14} /> Restaurer la chaîne
            </button>
          )}
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
            data-testid="audit-limit-select"
            disabled={tamperMode}
          >
            <option value={20}>20 événements</option>
            <option value={50}>50 événements</option>
            <option value={100}>100 événements</option>
            <option value={200}>200 événements</option>
            <option value={500}>500 événements</option>
          </select>
          <button
            onClick={load}
            className="btn text-sm px-4 py-2"
            data-testid="audit-refresh-btn"
          >
            Rafraîchir
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/audit/public-chain?limit=${limit}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-white/50 hover:text-white flex items-center gap-1"
            data-testid="audit-json-link"
          >
            <ExternalLink size={12} /> JSON brut
          </a>
        </div>

        {/* TAMPER DRILL BANNER */}
        {tamperMode && tamperInfo && (
          <div
            className="mb-6 rounded-xl border-2 border-warn/60 bg-warn/10 p-5"
            data-testid="audit-tamper-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-warn shrink-0 mt-0.5" size={22} />
              <div className="flex-1">
                <div className="font-heading font-semibold text-warn text-lg">Tamper Drill actif · Falsification simulée</div>
                <div className="text-sm text-white/70 mt-1">
                  Nous avons délibérément modifié <b>l&apos;événement #{tamperInfo.index}</b> (côté navigateur uniquement — la base de données réelle est intacte).
                  La vérification cryptographique va détecter l&apos;anomalie automatiquement et casser la chaîne à partir de cet index.
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-3 text-[11px] font-mono">
                  <div className="rounded-lg bg-black/40 p-3 border border-white/10">
                    <div className="text-[9px] uppercase tracking-widest text-up mb-1">Bytes originaux (backend)</div>
                    <div className="text-white/60 break-all">{tamperInfo.original.slice(0, 200)}…</div>
                  </div>
                  <div className="rounded-lg bg-down/10 p-3 border border-down/40">
                    <div className="text-[9px] uppercase tracking-widest text-down mb-1">Bytes falsifiés (navigateur)</div>
                    <div className="text-white break-all">{tamperInfo.mutated.slice(0, 200)}…</div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-white/50">
                  💡 <b>Insight auditeur :</b> voilà exactement ce qui se passerait si un attaquant (ou nous-mêmes) tentait de réécrire l&apos;historique. La chaîne SHA-256 détecte la falsification à l&apos;octet près.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VERIFY RESULT */}
        {verifyState === "ok" && verifyDetail && (
          <div
            className="mb-6 rounded-xl border border-up/40 bg-up/10 p-5 flex items-start gap-3"
            data-testid="audit-verify-ok"
          >
            <CheckCircle2 className="text-up shrink-0 mt-0.5" size={22} />
            <div>
              <div className="font-heading font-semibold text-up text-lg">Chaîne vérifiée · {verifyDetail.n_verified} événements</div>
              <div className="text-sm text-white/70 mt-1">
                Tous les hash SHA-256 recalculés dans <b>votre navigateur</b> correspondent aux valeurs enregistrées.
                Aucune manipulation détectée. Hash final :
              </div>
              <code className="text-xs font-mono text-accent-blue break-all mt-1 block">{verifyDetail.final_hash}</code>
            </div>
          </div>
        )}
        {verifyState === "broken" && verifyDetail && (
          <div
            className={`mb-6 rounded-xl border p-5 flex items-start gap-3 ${
              verifyDetail.tamper_drill
                ? "border-warn/40 bg-warn/10"
                : "border-down/40 bg-down/10"
            }`}
            data-testid="audit-verify-broken"
          >
            <XCircle className={`shrink-0 mt-0.5 ${verifyDetail.tamper_drill ? "text-warn" : "text-down"}`} size={22} />
            <div className="flex-1">
              <div className={`font-heading font-semibold text-lg ${verifyDetail.tamper_drill ? "text-warn" : "text-down"}`}>
                {verifyDetail.tamper_drill
                  ? `✅ Détection réussie · Falsification simulée capturée à l'index ${verifyDetail.broken_index}`
                  : `⚠ Chaîne rompue à l'index ${verifyDetail.broken_index}`}
              </div>
              {verifyDetail.tamper_drill && (
                <div className="text-sm text-white/70 mt-1">
                  La vérification client-side a immédiatement détecté la manipulation. En production réelle, un tel écart déclencherait une alerte SOC + kill-switch automatique.
                </div>
              )}
              <div className="text-xs font-mono text-white/70 mt-2 space-y-1">
                <div>prev_hash attendu : <span className="text-accent-blue">{verifyDetail.expected_prev_hash}</span></div>
                <div>prev_hash reçu :    <span className="text-warn">{verifyDetail.got_prev_hash}</span></div>
                <div>event_hash attendu : <span className="text-accent-blue">{verifyDetail.expected_event_hash}</span></div>
                <div>event_hash reçu :    <span className="text-warn">{verifyDetail.got_event_hash}</span></div>
              </div>
            </div>
          </div>
        )}

        {/* METADATA */}
        {chain && (
          <div className="grid md:grid-cols-4 gap-3 mb-6 text-xs">
            <MetaCard label="Événements chaîne totale" value={chain.total_events?.toLocaleString("fr-FR")} testId="audit-meta-total" />
            <MetaCard label="Affichés" value={chain.count_returned} testId="audit-meta-shown" />
            <MetaCard label="Algorithme" value={chain.chain_algorithm} testId="audit-meta-algo" />
            <MetaCard label="Forme canonique" value="JSON sorted keys" testId="audit-meta-canonical" />
          </div>
        )}

        {/* TABLE */}
        {loading && (
          <div className="text-center text-white/50 py-16" data-testid="audit-loading">
            <Loader2 className="animate-spin mx-auto mb-3" size={24} />
            Chargement de la chaîne…
          </div>
        )}
        {err && (
          <div className="rounded-xl border border-down/40 bg-down/10 p-5 text-down text-sm" data-testid="audit-err">
            Erreur : {err}
          </div>
        )}
        {chain && !loading && (
          <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden" data-testid="audit-table-wrap">
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead className="bg-white/[0.03] text-white/50 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="px-4 py-3 text-left">#</th>
                    <th className="px-4 py-3 text-left">Vérif.</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Timestamp</th>
                    <th className="px-4 py-3 text-left">prev_hash</th>
                    <th className="px-4 py-3 text-left">event_hash</th>
                    <th className="px-4 py-3 text-left">Payload (masqué)</th>
                  </tr>
                </thead>
                <tbody>
                  {chain.events.map((ev, i) => {
                    const st = rowStatus[i];
                    return (
                      <tr
                        key={i}
                        className={`border-t border-white/5 hover:bg-white/[0.02] ${st === "broken" ? "bg-down/10" : ""}`}
                        data-testid={`audit-row-${i}`}
                      >
                        <td className="px-4 py-2 text-white/40">{i}</td>
                        <td className="px-4 py-2">
                          {st === "ok" ? (
                            <CheckCircle2 size={14} className="text-up" data-testid={`audit-row-${i}-ok`} />
                          ) : st === "broken" ? (
                            <XCircle size={14} className="text-down" data-testid={`audit-row-${i}-broken`} />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-white/20" />
                          )}
                        </td>
                        <td className="px-4 py-2 text-accent-blue">{ev.event_type}</td>
                        <td className="px-4 py-2 text-white/50 whitespace-nowrap">{fmtTs(ev.timestamp)}</td>
                        <td className="px-4 py-2 text-white/50" title={ev.prev_hash}>{shortHash(ev.prev_hash)}</td>
                        <td className="px-4 py-2 text-white" title={ev.event_hash}>{shortHash(ev.event_hash)}</td>
                        <td className="px-4 py-2 text-white/60 max-w-xs truncate" title={JSON.stringify(ev.payload_masked)}>
                          {JSON.stringify(ev.payload_masked)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* METHODOLOGY */}
        <div className="mt-16 grid md:grid-cols-2 gap-8" data-testid="audit-methodology">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-blue mb-3">Méthodologie</div>
            <h3 className="font-heading text-2xl mb-4">Comment ça marche</h3>
            <ol className="space-y-3 text-sm text-white/70">
              <li><b className="text-white">1.</b> À chaque événement (signal généré, ordre exécuté, paramètre changé…), nous construisons un JSON canonique avec les clés triées.</li>
              <li><b className="text-white">2.</b> Nous calculons <code className="text-accent-blue">sha256(canonical_bytes)</code> et le stockons dans MongoDB avec le <code className="text-accent-blue">prev_hash</code> pointant vers l&apos;événement précédent.</li>
              <li><b className="text-white">3.</b> Vous téléchargez la chaîne ci-dessus. Pour chaque événement, votre navigateur re-hashe les mêmes bytes via <code className="text-accent-blue">crypto.subtle.digest</code> et vérifie l&apos;égalité.</li>
              <li><b className="text-white">4.</b> Chaque événement lie au précédent : altérer un seul brouille TOUS les hash suivants. Détection immédiate.</li>
            </ol>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-blue mb-3">Pourquoi ça compte</div>
            <h3 className="font-heading text-2xl mb-4">Pour auditeurs & investisseurs exigeants</h3>
            <ul className="space-y-3 text-sm text-white/70">
              <li><b className="text-up">✓</b> Aucune confiance requise — vous vérifiez vous-même.</li>
              <li><b className="text-up">✓</b> Intégrité cryptographique du fichier (hash SHA-256, non-répudiation temporelle) — <em>utile pour vos propres audits, ne remplace pas un ARM/TR réglementé</em>.</li>
              <li><b className="text-up">✓</b> Horodatage et intégrité vérifiables par tout tiers (auditeur, contrepartie).</li>
              <li><b className="text-up">✓</b> Zéro dépendance à un tiers de confiance (blockchain, notary…).</li>
              <li><b className="text-up">✓</b> Payloads sensibles masqués → conforme RGPD/CCPA sans casser la chaîne.</li>
            </ul>
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-white/40 flex flex-wrap justify-between gap-4">
          <div>© 2026 ANATRADEX — Zero-Trust Audit · <Link to="/" className="hover:text-white">Landing</Link></div>
          <div>
            Vérification cryptographique via <code className="text-accent-blue">{`window.crypto.subtle.digest("SHA-256")`}</code>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaCard({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="mt-1 font-mono font-semibold text-white text-sm">{value ?? "—"}</div>
    </div>
  );
}
